import { Controller } from "@hotwired/stimulus"

// Interactive audio guide: step sync, screenshot updates, transport controls.
export default class extends Controller {
  connect() {
    this.guideData = this.readGuideData()
    if (!this.guideData) return

    this.cacheElements()
    if (!this.hasRequiredElements()) return

    this.abortController = new AbortController()
    const { signal } = this.abortController

    this.audioPlayer.src = this.guideData.audio_url
    this.stepElements = this.scriptContainer.querySelectorAll(".guide-step")

    this.audioPlayPauseBtn.addEventListener("click", () => this.togglePlay(), { signal })
    this.screenshotPlayPauseBtn.addEventListener("click", () => this.togglePlay(), { signal })
    this.audioMuteUnmuteBtn.addEventListener("click", () => this.toggleMute(), { signal })
    this.audioPlayer.addEventListener("play", () => this.updatePlayButton(), { signal })
    this.audioPlayer.addEventListener("pause", () => this.updatePlayButton(), { signal })
    this.audioPlayer.addEventListener("volumechange", () => this.updateMuteButton(), { signal })
    this.audioPlayer.addEventListener("timeupdate", () => this.updateProgress(), { signal })
    this.audioProgressContainer.addEventListener("click", (e) => this.setProgress(e), { signal })
    this.audioPlayer.addEventListener(
      "loadedmetadata",
      () => {
        this.audioDurationEl.textContent = this.formatTime(this.audioPlayer.duration)
      },
      { signal }
    )

    this.stepElements.forEach((stepEl) => {
      stepEl.addEventListener(
        "click",
        () => {
          this.audioPlayer.currentTime = this.timeToSeconds(stepEl.dataset.startTime)
          if (this.audioPlayer.paused) this.audioPlayer.play()
        },
        { signal }
      )
    })

    if (this.guideData.steps.length > 0) {
      this.updateScreenshot(this.guideData.steps[0])
    }
    this.updateMuteButton()
  }

  disconnect() {
    this.abortController?.abort()
    this.audioPlayer?.pause()
  }

  readGuideData() {
    const el = document.getElementById("guide-player-data")
    if (!el?.textContent?.trim()) return null
    try {
      return JSON.parse(el.textContent)
    } catch {
      return null
    }
  }

  cacheElements() {
    this.scriptContainer = document.getElementById("guides-script")
    this.screenshotImage = document.getElementById("guides-screenshot-image")
    this.audioPlayer = document.getElementById("guides-audio-player")
    this.audioPlayPauseBtn = document.getElementById("guides-audio-play-pause-btn")
    this.audioPlayIcon = document.getElementById("guides-audio-play-icon")
    this.audioPauseIcon = document.getElementById("guides-audio-pause-icon")
    this.audioProgressBar = document.getElementById("guides-audio-progress-bar")
    this.audioProgressHandle = document.getElementById("guides-audio-progress-handle")
    this.audioProgressContainer = document.getElementById("guides-audio-progress-container")
    this.audioCurrentTimeEl = document.getElementById("guides-audio-current-time")
    this.audioDurationEl = document.getElementById("guides-audio-duration")
    this.screenshotPlayPauseBtn = document.getElementById("guides-screenshot-play-pause-btn")
    this.screenshotPlayIcon = document.getElementById("guides-screenshot-play-icon")
    this.screenshotPauseIcon = document.getElementById("guides-screenshot-pause-icon")
    this.screenshotOverlay = document.getElementById("guides-screenshot-overlay")
    this.audioMuteUnmuteBtn = document.getElementById("guides-audio-mute-unmute-btn")
    this.audioVolumeOnIcon = document.getElementById("guides-audio-volume-on-icon")
    this.audioVolumeOffIcon = document.getElementById("guides-audio-volume-off-icon")
  }

  hasRequiredElements() {
    return (
      this.audioPlayer &&
      this.scriptContainer &&
      this.audioPlayPauseBtn &&
      this.screenshotPlayPauseBtn &&
      this.audioMuteUnmuteBtn
    )
  }

  formatTime(time) {
    if (Number.isNaN(time)) return "00:00"
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
  }

  timeToSeconds(timeStr) {
    const [minutes, seconds] = timeStr.split(":").map(Number)
    return minutes * 60 + seconds
  }

  toggleMute() {
    this.audioPlayer.muted = !this.audioPlayer.muted
  }

  updateMuteButton() {
    if (this.audioPlayer.muted) {
      this.audioVolumeOnIcon.classList.add("hidden")
      this.audioVolumeOffIcon.classList.remove("hidden")
    } else {
      this.audioVolumeOnIcon.classList.remove("hidden")
      this.audioVolumeOffIcon.classList.add("hidden")
    }
  }

  togglePlay() {
    if (this.audioPlayer.paused) {
      this.audioPlayer.play()
    } else {
      this.audioPlayer.pause()
    }
  }

  updatePlayButton() {
    if (this.audioPlayer.paused) {
      this.audioPlayIcon.classList.remove("hidden")
      this.audioPauseIcon.classList.add("hidden")
      this.screenshotPlayIcon.classList.remove("hidden")
      this.screenshotPauseIcon.classList.add("hidden")
    } else {
      this.screenshotOverlay.classList.add("opacity-0", "group-hover:opacity-100")
      this.audioPlayIcon.classList.add("hidden")
      this.audioPauseIcon.classList.remove("hidden")
      this.screenshotPlayIcon.classList.add("hidden")
      this.screenshotPauseIcon.classList.remove("hidden")
    }
  }

  updateProgress() {
    const progressPercentage = (this.audioPlayer.currentTime / this.audioPlayer.duration) * 100
    this.audioProgressBar.style.width = `${progressPercentage}%`
    this.audioProgressHandle.style.left = `${progressPercentage}%`
    this.audioCurrentTimeEl.textContent = this.formatTime(this.audioPlayer.currentTime)
    this.highlightActiveStep()
  }

  setProgress(event) {
    const rect = this.audioProgressContainer.getBoundingClientRect()
    const offsetX = event.clientX - rect.left
    const width = rect.width
    const duration = this.audioPlayer.duration
    this.audioPlayer.currentTime = (offsetX / width) * duration
  }

  highlightActiveStep() {
    const currentTime = this.audioPlayer.currentTime
    let activeStep = null

    for (let i = 0; i < this.guideData.steps.length; i++) {
      const step = this.guideData.steps[i]
      const nextStep = this.guideData.steps[i + 1]
      const startTime = this.timeToSeconds(step.start_time)
      const endTime = nextStep ? this.timeToSeconds(nextStep.start_time) : this.audioPlayer.duration

      if (currentTime >= startTime && (currentTime < endTime || !this.audioPlayer.duration)) {
        activeStep = step
      }
    }

    this.stepElements.forEach((stepEl) => {
      const stepId = parseInt(stepEl.dataset.id, 10)
      if (activeStep && stepId === activeStep.id) {
        if (!stepEl.classList.contains("active")) {
          stepEl.classList.add("active")
          stepEl.scrollIntoView({ behavior: "smooth", block: "center" })
          this.updateScreenshot(activeStep)
        }
      } else {
        stepEl.classList.remove("active")
      }
    })
  }

  updateScreenshot(step) {
    if (step) {
      const screenshotUrl = `https://storage.googleapis.com/biddyhq/guides-${this.guideData.guide_id}-${step.id}.png?timestamp=${new Date().getTime()}`
      this.screenshotImage.src = screenshotUrl
      this.screenshotImage.style.display = "block"

      this.screenshotImage.onerror = () => {
        this.screenshotImage.style.display = "none"
      }
    } else {
      this.screenshotImage.src = ""
      this.screenshotImage.style.display = "none"
    }
  }
}
